import { useEffect, useLayoutEffect, useState } from 'react';

/** Matches `--motion-medium` in index.css */
const DEFAULT_DURATION_MS = 360;

function getPresenceDuration(durationMs) {
    if (typeof window === 'undefined') return durationMs;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : durationMs;
}

/**
 * Keeps UI mounted through CSS exit transitions.
 * Mount with `visible=false`, then flip `visible` after layout so enter animates;
 * on close, flip `visible` off and unmount after `durationMs`.
 */
export function useAnimatedPresence(open, durationMs = DEFAULT_DURATION_MS) {
    const [mounted, setMounted] = useState(open);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (open) {
            setMounted(true);
            return undefined;
        }

        setVisible(false);
        const exitMs = getPresenceDuration(durationMs);
        const exitTimer = window.setTimeout(() => setMounted(false), exitMs);
        return () => window.clearTimeout(exitTimer);
    }, [open, durationMs]);

    useLayoutEffect(() => {
        if (!mounted || !open) return undefined;

        setVisible(false);
        let enterRaf = 0;
        enterRaf = requestAnimationFrame(() => {
            enterRaf = requestAnimationFrame(() => setVisible(true));
        });

        return () => cancelAnimationFrame(enterRaf);
    }, [mounted, open]);

    return { mounted, visible };
}
